import { Lead, Message, Conversation, ScheduledMessage, Note, InternalReminder, AvatarRefreshResult, KanbanColumn, FollowUpStats, FollowUpLeadsResponse } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';

const API_BASE = '/api';

let cachedToken: string | null = null;
let cachedTokenTs = 0;
const TOKEN_CACHE_TTL = 55 * 60 * 1000; // 55 minutos (token JWT dura 60 min)

export function getCachedToken(): string | null {
  if (cachedToken && Date.now() - cachedTokenTs < TOKEN_CACHE_TTL) {
    return cachedToken;
  }
  return null;
}

export async function getValidAuthToken(): Promise<string | null> {
  let token = getCachedToken();
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      if (expiresAt - Date.now() < 5 * 60 * 1000 && supabase) {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data?.session?.access_token) {
          token = data.session.access_token;
          setCachedToken(token);
          return token;
        }
      }
      return token;
    } catch (e) {
      // payload parse failed, ignore
    }
  }
  return await waitForSession(3000);
}

export function clearCachedToken() {
  cachedToken = null;
  cachedTokenTs = 0;
}

// Prime o cache com o token fresco que o Supabase entrega no onAuthStateChange.
// Evita a janela de 401 no boot: o evento passa a re-preencher o cache em vez de só limpar.
export function primeAuthToken(token: string | null) {
  if (token) {
    setCachedToken(token);
  }
  // DD1: NÃO limpar cache quando token é null.
  // O Supabase dispara onAuthStateChange com session=null em transições internas
  // normais (ex: durante o próprio auto-refresh). Zerar o cache nesse momento
  // forçava reconsulta de sessão à toa e contribuía pro 401 em cascata.
  // O cache expira sozinho pelo TOKEN_CACHE_TTL (55min).
}

function setCachedToken(token: string) {
  cachedToken = token;
  cachedTokenTs = Date.now();
}

// FIX (P4.2): Promise unificada pra evitar loops paralelos de waitForSession.
// Se 5 requests falham com 401 ao mesmo tempo, todas aguardam a MESMA promise
// em vez de disparar 5 loops independentes com 50 chamadas/segundo.
let sessionPromise: Promise<string | null> | null = null;
let refreshPromise: Promise<any> | null = null; // DD1: mutex do refreshSession no handler 401

async function waitForSession(maxWait = 1000): Promise<string | null> {
  const cached = getCachedToken();
  if (cached) return cached;

  // Se já tem uma promise em andamento, aguardar ela (não criar nova)
  if (sessionPromise) return sessionPromise;

  sessionPromise = (async () => {
    try {
      // FIX (P4.3): Promise.race com timeout de 5s pra evitar travamento eterno.
      // Se o refresh do Supabase não responder em 5s, a promise resolve null
      // e as requests podem prosseguir (ou falhar com 401) em vez de ficar esperando.
      const timeoutPromise = new Promise<string | null>((resolve) => 
        setTimeout(() => resolve(null), 5000)
      );
      
      const realSessionPromise = (async () => {
        try {
          if (!supabase) return null;
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) {
            console.warn('[API] getSession error:', error.message);
          }
          if (session?.access_token) {
            setCachedToken(session.access_token);
            return session.access_token;
          }

          // Se getSession veio vazio no boot (F5), forçar refresh a partir do
          // refresh_token persistido no storage antes de desistir. Evita 401 em cascata.
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          if (refreshed?.access_token) {
            setCachedToken(refreshed.access_token);
            return refreshed.access_token;
          }
          return null;
        } catch (e) {
          console.error('[API] Error in realSessionPromise:', e);
          return null;
        }
      })();
      
      return await Promise.race([realSessionPromise, timeoutPromise]);
    } catch (e) {
      console.error('[API] Error in waitForSession:', e);
      return null;
    } finally {
      // Limpa a promise pra próxima vez
      sessionPromise = null;
    }
  })();

  return sessionPromise;
}

let isRedirectingToLogin = false;
let redirectingSince = 0;
const REDIRECT_STUCK_TIMEOUT_MS = 5000; // se travar mais que isso, é bug/bfcache — libera

async function fetchWithRetry(url: string, options: RequestInit = {}, retries: number = 3, backoff: number = 300, authRetries: number = 1, requestTimeoutMs?: number): Promise<Response> {
  try {
    const headers = new Headers(options.headers || {});
    
    // Set x-loja-id header if active loja ID is present
    try {
      // Bind explicit request scope before awaiting auth/import. Retries keep this header.
      let lojaId = new URL(url, 'http://localhost').searchParams.get('loja_id');
      if (!lojaId && !headers.has('x-loja-id') && typeof window !== 'undefined') {
        const { useLojaAtivaStore } = await import('@/store/lojaAtivaStore');
        lojaId = useLojaAtivaStore.getState().lojaAtivaId;
      }
      if (lojaId) {
        headers.set('x-loja-id', lojaId);
      }
    } catch (err) {
      // Ignore
    }

    if (!headers.has('Authorization')) {
      const token = await waitForSession(1000);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const isUpload = options.body instanceof FormData;
    const timeoutMs = requestTimeoutMs ?? (isUpload ? 60000 : 30000);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort(new DOMException('Tempo limite da requisição excedido (timeout)', 'TimeoutError'));
    }, timeoutMs);
    
    let cleanupSignalListener: (() => void) | null = null;
    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort(options.signal.reason || new DOMException('Requisição cancelada', 'AbortError'));
      } else {
        const onAbort = () => controller.abort(options.signal?.reason || new DOMException('Requisição cancelada', 'AbortError'));
        options.signal.addEventListener('abort', onAbort);
        cleanupSignalListener = () => {
          options.signal?.removeEventListener('abort', onAbort);
        };
      }
    }
    const signal = controller.signal;

    let res: Response;
    try {
      res = await fetch(url, { ...options, headers, signal });
    } finally {
      clearTimeout(timeoutId);
      if (cleanupSignalListener) {
        cleanupSignalListener();
      }
    }
    
    const isHtml = res.headers.get('content-type')?.includes('text/html');
    const isApiRequest = url.includes('/api/');
    
    if (isApiRequest && isHtml && retries > 0) {
      await new Promise(r => setTimeout(r, backoff));
      return fetchWithRetry(url, { ...options, headers }, retries - 1, backoff * 2, authRetries, requestTimeoutMs);
    }

    if (!res.ok && (res.status === 502 || res.status === 503 || res.status === 504) && retries > 0) {
      await new Promise(r => setTimeout(r, backoff));
      return fetchWithRetry(url, { ...options, headers }, retries - 1, backoff * 2, authRetries, requestTimeoutMs);
    }

    // BB1: Handler de 401 dedicado (não depende de retries genéricos)
    if (!res.ok && res.status === 401) {
      cachedToken = null;
      cachedTokenTs = 0;
      
      // Tentar refresh UMA vez
      if (supabase && authRetries > 0) {
        if (!refreshPromise) {
          refreshPromise = supabase.auth.refreshSession();
        }
        try {
          const { data: refreshData, error: refreshErr } = await refreshPromise;
          if (!refreshErr && refreshData?.session?.access_token) {
            setCachedToken(refreshData.session.access_token);
            headers.set('Authorization', `Bearer ${refreshData.session.access_token}`);
            return fetchWithRetry(url, { ...options, headers }, retries, backoff * 2, authRetries - 1, requestTimeoutMs);
          }
        } finally {
          // IMPORTANTE: sempre liberar o mutex, mesmo se refreshSession() lançar
          // exceção — senão o mutex fica travado pra sempre e nenhum 401 futuro
          // consegue mais tentar refresh.
          refreshPromise = null;
        }
      }
      
      // Se refresh falhou, SEMPRE fazer logout (não depende de retries)
      // GG1: se a flag estiver "true" há mais tempo que o esperado pro redirect
      // acontecer de verdade, é sinal de que travou (ex: aba congelada pelo
      // navegador no meio do processo) — libera e tenta de novo.
      if (isRedirectingToLogin && (Date.now() - redirectingSince > REDIRECT_STUCK_TIMEOUT_MS)) {
        isRedirectingToLogin = false;
      }
      if (supabase && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        redirectingSince = Date.now();
        console.warn('[API] Sessão expirada, deslogando...');
        await supabase.auth.signOut().catch(() => {});
        window.location.href = '/login?motivo=sessao_expirada';
      }
      return res;
    }
    
    return res;
  } catch (err: any) {
    if (err?.name === 'AbortError' || options?.signal?.aborted) {
      throw err;
    }
    if (retries > 0) {
      await new Promise(r => setTimeout(r, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2, authRetries, requestTimeoutMs);
    }
    throw err;
  }
}

export async function fetchWithAuth(url: string, options: RequestInit = {}, policy?: { retries?: number; timeoutMs?: number }): Promise<Response> {
  const token = await waitForSession(1000);
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // FIX (P0.3): injetar Content-Type: application/json automaticamente quando
  // há body e o caller não especificou o header.
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let res = await fetchWithRetry(url, { ...options, headers }, policy?.retries ?? 3, 300, policy?.retries === 0 ? 0 : 1, policy?.timeoutMs);

  // Tratamento de 401: renovação de token e retry uma única vez
  if (res.status === 401 && policy?.retries !== 0) {
    try {
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        const newToken = session?.access_token;
        if (session && newToken && newToken !== token) {
          setCachedToken(newToken);
          const newHeaders = new Headers(headers);
          newHeaders.set('Authorization', `Bearer ${newToken}`);
          res = await fetchWithRetry(url, { ...options, headers: newHeaders }, policy?.retries ?? 3, 300, 1, policy?.timeoutMs);
        }
      }
    } catch (e) {
      console.warn('[API] Erro ao buscar sessão no tratamento de 401:', e);
    }

    if (res.status === 401) {
      if (isRedirectingToLogin && (Date.now() - redirectingSince > REDIRECT_STUCK_TIMEOUT_MS)) {
        isRedirectingToLogin = false;
      }
      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;
        redirectingSince = Date.now();
        toast.error('Sessão expirada — faça login novamente');
        if (supabase) {
          await supabase.auth.signOut().catch(() => {});
        }
        window.location.href = '/login?motivo=sessao_expirada';
      }
    }
  }

  if (res.status === 403) {
    try {
      const clone = res.clone();
      const body = await clone.json();
      if (body?.code === 'PLAN_LIMIT_EXCEEDED' || body?.error?.toLowerCase().includes('plano') || body?.error?.toLowerCase().includes('limite')) {
        window.dispatchEvent(new CustomEvent('plan-limit-exceeded', {
          detail: { message: body.error || 'Limite do plano atingido.' }
        }));
      }
    } catch (e) {
      // ignore JSON parse error
    }
  }

  return res;
}

export const apiCall = fetchWithAuth;

export interface AuditLog {
  id: string;
  lead_id: string;
  actor_type: string;
  actor_id?: string;
  acao: string;
  after_data?: any;
  created_at: string | null;
}

export interface LeadsPageResponse {
  items: Lead[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount?: number;
}

export interface GetLeadsPageParams {
  cursor?: string | null;
  limit?: number;
  loja_id?: string | null;
  tenant_id?: string | null;
  etapa_id?: string | null;
  status?: string | null;
  responsavel_user_id?: string | null;
  q?: string | null;
  signal?: AbortSignal;
}

export interface MessagesPageResponse {
  items: Message[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface GetMessagesPageParams {
  cursor?: string | null;
  limit?: number;
  lojaId?: string | null;
  tenantId?: string | null;
  signal?: AbortSignal;
}

export interface ConversationsPageResponse {
  items: Conversation[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface GetConversationsPageParams {
  cursor?: string | null;
  limit?: number;
  loja_id?: string | null;
  tenant_id?: string | null;
  connection_id?: string | null;
  archived?: boolean;
  transbordo?: boolean;
  my_only?: boolean;
  q?: string;
  signal?: AbortSignal;
}

export const api = {
  getAuditLogs: async (leadId: string): Promise<{ items: AuditLog[]; truncated: boolean; sourceLimit: number }> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${encodeURIComponent(leadId)}/audit-logs`);
    if (!res.ok) throw new Error(res.status === 403 ? 'Acesso negado ao histórico.' : 'Não foi possível carregar o histórico.');
    const data = await res.json();
    if (!Array.isArray(data?.items)) throw new Error('Resposta inválida do histórico.');
    return data;
  },

  getConversationByLeadId: async (leadId: string): Promise<any | null> => {
    // FIX (migração Resgata 2026-10-19): consulta direta por lead_id no servidor
    // em vez de hidratar as primeiras 500 conversas — acima disso, leads antigos
    // não eram encontrados e o chat não carregava.
    const res = await fetchWithRetry(`${API_BASE}/conversations?lead_id=${encodeURIComponent(leadId)}&limit=1`);
    // null = lead sem conversa (ex.: importado por planilha). Falha HTTP precisa
    // propagar o motivo real em vez de se passar por "sem conversa".
    if (!res.ok) {
      const body = await res.json().catch(() => ({} as any));
      throw new Error(body?.error || body?.message || `HTTP ${res.status}`);
    }
    const rows = await res.json();
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  },

  getLeadById: async (leadId: string): Promise<Lead> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${encodeURIComponent(leadId)}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({} as any));
      throw new Error(body?.error || body?.message || `HTTP ${res.status}`);
    }
    return await res.json();
  },

  getConversationsPage: async (
    params?: GetConversationsPageParams
  ): Promise<ConversationsPageResponse> => {
    const queryParts: string[] = ['paginacao=cursor'];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.cursor) queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    if (params?.loja_id) queryParts.push(`loja_id=${encodeURIComponent(params.loja_id)}`);
    if (params?.tenant_id) queryParts.push(`tenant_id=${encodeURIComponent(params.tenant_id)}`);
    if (params?.connection_id) queryParts.push(`connection_id=${encodeURIComponent(params.connection_id)}`);
    if (params?.archived !== undefined) queryParts.push(`archived=${params.archived}`);
    if (params?.transbordo) queryParts.push('transbordo=true');
    if (params?.my_only) queryParts.push('my_only=true');
    if (params?.q) queryParts.push(`q=${encodeURIComponent(params.q)}`);

    const url = `${API_BASE}/conversations?${queryParts.join('&')}`;
    const res = await fetchWithRetry(url, { signal: params?.signal });
    if (!res.ok) {
      const err: any = new Error(`Falha ao buscar página de conversas: ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('O servidor retornou HTML para /api/conversations.');
    }
    return res.json();
  },

  getConversations: async (params?: { loja_id?: string; tenant_id?: string; connection_id?: string; archived?: boolean; q?: string; my_only?: boolean; transbordo?: boolean; limit?: number; offset?: number }): Promise<any[]> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
    }
    const query = searchParams.toString();
    const res = await fetchWithRetry(`${API_BASE}/conversations${query ? '?' + query : ''}`);
    if (!res.ok) return [];
    return res.json();
  },

  getConversationsCount: async (params?: { loja_id?: string; tenant_id?: string; connection_id?: string; archived?: boolean; transbordo?: boolean; my_only?: boolean }): Promise<number> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
    }
    const query = searchParams.toString();
    const res = await fetchWithRetry(`${API_BASE}/conversations/count${query ? '?' + query : ''}`);
    if (!res.ok) return 0;
    const data = await res.json();
    return typeof data?.count === 'number' ? data.count : 0;
  },

  markLeadAsRead: async (id: string): Promise<any> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${id}/read`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha ao marcar lead como lido' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return await res.json().catch(() => ({ success: true }));
  },

  markConversationAsRead: async (id: string): Promise<any> => {
    const res = await fetchWithRetry(`${API_BASE}/conversations/${id}/read`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha ao marcar conversa como lida' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return await res.json().catch(() => ({ success: true }));
  },

  recoverMessageMedia: async (messageId: string): Promise<{ success: boolean; recovered?: boolean; media_url?: string; error?: string }> => {
    const res = await fetchWithRetry(`${API_BASE}/messages/${messageId}/media/recover`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha ao recuperar mídia' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
  },

  updateLeadFunnelStage: async (id: string, etapa_id: string): Promise<void> => {
    await fetchWithRetry(`${API_BASE}/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ etapa_id })
    });
  },

  updateLeadAgent: async (id: string): Promise<void> => {
    const user = useAuthStore.getState().user;
    await fetchWithRetry(`${API_BASE}/leads/${id}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user?.id })
    });
  },

  createLead: async (data: { nome: string; telefone: string; email?: string; loja_id?: string; origem?: string }): Promise<Lead> => {
    const res = await fetchWithRetry(`${API_BASE}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Falha ao criar lead');
    }
    return res.json();
  },

  getLeadsPage: async (params?: GetLeadsPageParams): Promise<LeadsPageResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('paginacao', 'cursor');
    if (params) {
      if (params.cursor) searchParams.append('cursor', params.cursor);
      if (params.limit) searchParams.append('limit', String(params.limit));
      if (params.loja_id) searchParams.append('loja_id', params.loja_id);
      if (params.tenant_id) searchParams.append('tenant_id', params.tenant_id);
      if (params.etapa_id) searchParams.append('etapa_id', params.etapa_id);
      if (params.status) searchParams.append('status', params.status);
      if (params.responsavel_user_id) searchParams.append('responsavel_user_id', params.responsavel_user_id);
      if (params.q) searchParams.append('q', params.q);
    }
    const query = searchParams.toString();
    const res = await fetchWithRetry(`${API_BASE}/leads?${query}`, {
      signal: params?.signal
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      const err: any = new Error(errBody.error || `Falha ao buscar página de leads: ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  getLeads: async (params?: { limit?: number; offset?: number; status?: string; q?: string; loja_id?: string; etapa_id?: string; responsavel_user_id?: string }): Promise<{ data: Lead[]; totalCount: number }> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
    }
    const query = searchParams.toString();
    const res = await fetchWithRetry(`${API_BASE}/leads${query ? '?' + query : ''}`);
    if (!res.ok) throw new Error(`Falha ao buscar leads: ${res.status}`);
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`O servidor retornou HTML em vez de JSON para /api/leads. Verifique se o backend está rodando corretamente.`);
    }
    const data = await res.json();
    const totalHeader = res.headers.get('X-Total-Count') || res.headers.get('x-total-count');
    const items = Array.isArray(data) ? data : [];
    const totalCount = totalHeader ? parseInt(totalHeader, 10) : items.length;
    return { data: items, totalCount };
  },

  getLeadsAggregateStats: async (params?: { tenant_id?: string; loja_id?: string; start_date?: string; end_date?: string }): Promise<{
    total_leads: number;
    unread_count: number;
    unassigned_count: number;
    new_leads_today: number;
    new_leads_7d: number;
    new_leads_30d: number;
    new_leads_period: number;
    by_status: Record<string, number>;
    by_etapa: Record<string, number>;
    by_origem: Record<string, number>;
    by_loja: Record<string, number>;
    by_etapa_including_unassigned: Record<string, number>;
    by_etapa_tipo: Record<string, number>;
    ganhos: number;
    perdidos: number;
    qualificados: number;
    em_atendimento: number;
    sem_resposta_24h: number;
  } | null> => {
    try {
      const searchParams = new URLSearchParams({ view: 'kanban' });
      if (params?.tenant_id) searchParams.append('tenant_id', params.tenant_id);
      if (params?.loja_id) searchParams.append('loja_id', params.loja_id);
      if (params?.start_date) searchParams.append('start_date', params.start_date);
      if (params?.end_date) searchParams.append('end_date', params.end_date);
      const query = searchParams.toString();
      const res = await fetchWithRetry(`${API_BASE}/dashboard/aggregate-stats${query ? '?' + query : ''}`);
      if (!res.ok) {
        console.warn('[API] getLeadsAggregateStats HTTP error:', res.status);
        return null;
      }
      const data = await res.json();
      return {
        total_leads: Number(data.total_leads) || 0,
        unread_count: Number(data.unread_count) || 0,
        unassigned_count: Number(data.unassigned_count) || 0,
        new_leads_today: Number(data.new_leads_today) || 0,
        new_leads_7d: Number(data.new_leads_7d) || 0,
        new_leads_30d: Number(data.new_leads_30d) || 0,
        new_leads_period: Number(data.new_leads_period) || 0,
        by_status: (data.by_status && typeof data.by_status === 'object') ? data.by_status as Record<string, number> : {},
        by_etapa: (data.by_etapa && typeof data.by_etapa === 'object') ? data.by_etapa as Record<string, number> : {},
        by_origem: (data.by_origem && typeof data.by_origem === 'object') ? data.by_origem as Record<string, number> : {},
        by_loja: (data.by_loja && typeof data.by_loja === 'object') ? data.by_loja as Record<string, number> : {},
        by_etapa_including_unassigned: (data.by_etapa_including_unassigned && typeof data.by_etapa_including_unassigned === 'object') ? data.by_etapa_including_unassigned as Record<string, number> : {},
        by_etapa_tipo: (data.by_etapa_tipo && typeof data.by_etapa_tipo === 'object') ? data.by_etapa_tipo as Record<string, number> : {},
        ganhos: Number(data.ganhos) || 0,
        perdidos: Number(data.perdidos) || 0,
        qualificados: Number(data.qualificados) || 0,
        em_atendimento: Number(data.em_atendimento) || 0,
        sem_resposta_24h: Number(data.sem_resposta_24h) || 0,
      };
    } catch (e: any) {
      console.warn('[API] getLeadsAggregateStats exception:', e?.message || e);
      return null;
    }
  },

  getFollowUpStats: async (): Promise<FollowUpStats> => {
    const res = await fetchWithRetry(`${API_BASE}/follow-up/stats`);
    if (!res.ok) throw new Error('Falha ao buscar estatísticas do follow-up');
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('O servidor retornou HTML em vez de JSON para /api/follow-up/stats. Verifique se o backend está rodando.');
    }
    return res.json();
  },

  getFollowUpLeads: async (params?: { filter?: string; q?: string; limit?: number; offset?: number }): Promise<FollowUpLeadsResponse> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
    }
    const query = searchParams.toString();
    const res = await fetchWithRetry(`${API_BASE}/follow-up/leads${query ? '?' + query : ''}`);
    if (!res.ok) throw new Error('Falha ao buscar leads de follow-up');
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('O servidor retornou HTML em vez de JSON para /api/follow-up/leads. Verifique se o backend está rodando.');
    }
    return res.json();
  },

  getLeadPreviews: async (remotejids: string[]): Promise<Record<string, { preview: string, time: string }>> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/previews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remotejids })
    });
    if (!res.ok) return {};
    if (res.headers.get('content-type')?.includes('text/html')) return {};
    return await res.json();
  },

  updateLeadStatus: async (id: string, status: string): Promise<Lead> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status_atendimento: status })
    });
    if (!res.ok) throw new Error('Failed to update lead status');
    return await res.json();
  },

  updateLead: async (id: string, data: Partial<Lead> & { expected_custom_fields_revision?: string }): Promise<Lead> => {
    console.log('[API updateLead]', id, data);
    const res = await fetchWithRetry(`${API_BASE}/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update lead');
    if (res.headers?.get('X-CRM-Audit-Warning') === 'audit-write-failed') {
      toast.error('Alteração salva, mas o registro de auditoria falhou. Não repita a alteração.');
    }
    return await res.json();
  },

  assignLead: async (id: string, userId: string | null): Promise<any> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${id}/assign-full`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    if (!res.ok) throw new Error('Falha ao atribuir responsável');
    return await res.json();
  },

  deleteLead: async (id: string): Promise<{ success: boolean; message: string }> => {
    // 60s: leads com muitas mensagens; o retry por timeout/5xx é seguro porque 404 = já apagado.
    const res = await fetchWithRetry(`${API_BASE}/leads/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }, 3, 300, 1, 60000);
    // Idempotente: repetição/retry de um lead já apagado não é erro para quem pediu a exclusão.
    if (res.status === 404) return { success: true, message: 'Lead já havia sido removido.' };
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as any));
      throw new Error(err.error ? `Falha ao deletar lead: ${err.error}` : `Falha ao deletar lead (HTTP ${res.status})`);
    }
    return await res.json();
  },
  
  getNotes: async (leadId: string): Promise<Note[]> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/notes`);
    if (!res.ok) throw new Error('Falha ao buscar anotações');
    return res.json();
  },
  
  addNote: async (leadId: string, note: string, categoria?: string): Promise<Note> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note, categoria: categoria || 'geral' })
    });
    if (!res.ok) throw new Error('Falha ao salvar anotação');
    const saved = await res.json();
    if (!saved?.id || saved.lead_id !== leadId) throw new Error('Anotação sem confirmação de persistência. Confira o histórico antes de tentar novamente.');
    return saved;
  },

  updateNote: async (noteId: string, note: string): Promise<Note> => {
    const res = await fetchWithRetry(`${API_BASE}/notes/${noteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note })
    });
    if (!res.ok) throw new Error('Falha ao editar anotação');
    return res.json();
  },

  deleteNote: async (noteId: string): Promise<void> => {
    const res = await fetchWithRetry(`${API_BASE}/notes/${noteId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Falha ao excluir anotação');
  },

  getReminders: async (leadId: string): Promise<InternalReminder[]> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/reminders`);
    if (!res.ok) throw new Error('Falha ao buscar lembretes');
    return res.json();
  },

  addReminder: async (leadId: string, reminder: { title: string; description?: string; remind_at: string }): Promise<InternalReminder> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/reminders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reminder)
    });
    if (!res.ok) throw new Error('Falha ao criar lembrete');
    return res.json();
  },

  updateReminder: async (id: number, updates: Partial<InternalReminder>): Promise<InternalReminder> => {
    const res = await fetchWithRetry(`${API_BASE}/reminders/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Falha ao atualizar lembrete');
    return res.json();
  },

  getMessages: async (leadId: string, limit: number = 100, offset: number = 0, signal?: AbortSignal): Promise<Message[]> => {
    if (!leadId) return [];
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/messages?limit=${limit}&offset=${offset}`, { signal });
    if (!res.ok) throw new Error(`Falha ao buscar mensagens: ${res.status}`);
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`O servidor retornou HTML para /api/messages.`);
    }
    return res.json();
  },

  getMessagesPage: async (
    leadId: string,
    params?: GetMessagesPageParams
  ): Promise<MessagesPageResponse> => {
    if (!leadId) return { items: [], nextCursor: null, hasMore: false };
    const queryParts: string[] = ['paginacao=cursor'];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.cursor) queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    if (params?.lojaId) queryParts.push(`loja_id=${encodeURIComponent(params.lojaId)}`);
    if (params?.tenantId) queryParts.push(`tenant_id=${encodeURIComponent(params.tenantId)}`);

    const url = `${API_BASE}/leads/${leadId}/messages?${queryParts.join('&')}`;
    // Query owns transient retries; nesting 4 HTTP attempts underneath it multiplies delays.
    const res = await fetchWithRetry(url, { signal: params?.signal }, 0);
    if (!res.ok) {
      const err: any = new Error(`Falha ao buscar página de mensagens: ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`O servidor retornou HTML para /api/leads/${leadId}/messages.`);
    }
    return res.json();
  },
  
  controlConversationAi: async (
    conversationId: string,
    command: 'reset' | 'resume' | 'pause',
    requestId: string = crypto.randomUUID()
  ): Promise<{ success: boolean; duplicate?: boolean; confirmation?: string | null }> => {
    const response = await fetchWithRetry(`${API_BASE}/conversations/${conversationId}/ai-control`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command, request_id: requestId })
    });
    const result = await response.json();
    if (!response.ok || result.success !== true) {
      throw new Error(result.error || result.code || 'Controle da IA não confirmado');
    }
    return result;
  },

  sendMessage: async (
    leadId: string, 
    content: string, 
    remotejid?: string, 
    messageType: Message['message_type'] = 'text', 
    mediaUrl?: string,
    quotedMsgId?: string,
    quotedMsgContent?: string,
    isPrivate?: boolean,
    operationId: string = crypto.randomUUID()
  ): Promise<Message> => {
    const res = await fetchWithRetry(`${API_BASE}/messages/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        leadId,
        content,
        remotejid,
        messageType,
        mediaUrl,
        quotedMsgId,
        quotedMsgContent,
        operationId,
        isPrivate
      })
    });

    if (!res.ok) {
      if (res.headers.get('content-type')?.includes('text/html')) {
        throw new Error(`Endpoint returned HTML: /api/messages/send`);
      }
      const errData = await res.json();
      throw Object.assign(new Error(errData.code || errData.error || 'Failed to send message'), { retryable: errData.retryable, requiresReview: errData.requiresReview, outcome: errData.outcome });
    }
    
    if (res.headers.get('content-type')?.includes('text/html')) {
      throw new Error(`Endpoint returned HTML: /api/messages/send (200 OK)`);
    }

    const resData = await res.json();
    if (resData.transportAccepted && resData.inboxPersistence?.status === 'failed') {
      throw new Error('Aceito pelo WhatsApp; histórico indisponível. Não reenviar; revisar sincronização.');
    }
    const msg = resData.data || resData;
    if (msg && typeof msg === 'object') {
      if (resData.send_status === 'failed') {
        msg.send_status = 'failed';
        msg.error = resData.error;
      }
    }
    return msg as Message;
  },

  reactToMessage: async (msgId: string, emoji: string): Promise<void> => {
    // Não chamado pela UI.
    await fetchWithRetry(`${API_BASE}/messages/${msgId}/react`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emoji })
    });
  },

  deleteMessage: async (msgId: string): Promise<void> => {
    // Não chamado pela UI.
    await fetchWithRetry(`${API_BASE}/messages/${msgId}`, {
      method: 'DELETE'
    });
  },

  setPresence: async (connectionId: string, remotejid: string, presence: "composing" | "paused"): Promise<void> => {
    await fetchWithRetry(`${API_BASE}/whatsapp/connections/${connectionId}/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remotejid, presence })
    }).catch(err => {
      console.warn('[api.setPresence] error:', err);
    });
  },

  getConfigs: async (): Promise<any> => {
    const res = await fetchWithRetry(`${API_BASE}/configs`);
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.warn('Configs endpoint returned HTML instead of JSON');
      return null;
    }
    return await res.json();
  },

  updateConfigs: async (data: any): Promise<void> => {
    await fetchWithRetry(`${API_BASE}/configs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  
  uploadAudio: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('audio', file);
    
    const res = await fetchWithRetry(`${API_BASE}/uploads/audio`, {
      method: 'POST',
      body: formData
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha no upload' }));
      throw new Error(err.error || 'Falha no upload');
    }
    return res.json();
  },

  uploadGeneric: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetchWithRetry(`${API_BASE}/uploads/generic`, {
      method: 'POST',
      body: formData
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha no upload do arquivo' }));
      throw new Error(err.error || 'Falha no upload do arquivo');
    }
    return res.json();
  },

  uploadFile: async (file: File): Promise<{ url?: string; publicUrl?: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetchWithRetry(`${API_BASE}/uploads/generic`, {
      method: 'POST',
      body: formData
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha no upload do arquivo' }));
      throw new Error(err.error || 'Falha no upload do arquivo');
    }
    return res.json();
  },
  
  refreshAvatar: async (leadId: string, force: boolean = false): Promise<AvatarRefreshResult> => {
    const res = await fetch(`${API_BASE}/leads/${leadId}/avatar/refresh?force=${force}`, {
      method: 'POST'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha ao atualizar foto' }));
      throw new Error(err.error || 'Falha ao atualizar foto');
    }
    return res.json();
  },

  hydrateAvatars: async (params?: { lead_ids?: string[]; limit?: number; force?: boolean }): Promise<any> => {
    const res = await fetch(`${API_BASE}/leads/avatars/hydrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params || {})
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Falha na hidratação de avatares' }));
      throw new Error(err.error || 'Falha na hidratação de avatares');
    }
    return res.json();
  },
  
  scheduleMessage: async (leadId: string, remotejid: string, message: string, scheduledAt: string, messageType: 'text' | 'audio' = 'text', mediaUrl?: string): Promise<any> => {
    const res = await fetchWithRetry(`${API_BASE}/crm-scheduled-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        lead_id: leadId, 
        remotejid,
        message, 
        scheduled_at: scheduledAt, 
        message_type: messageType, 
        media_url: mediaUrl 
      })
    });
    if (!res.ok) throw new Error('Falha ao agendar mensagem');
    return res.json();
  },

  getScheduledMessages: async (leadId: string): Promise<ScheduledMessage[]> => {
    const res = await fetchWithRetry(`${API_BASE}/leads/${leadId}/crm-scheduled-messages`);
    if (!res.ok) throw new Error('Falha ao buscar agendamentos');
    return res.json();
  },

  cancelScheduledMessage: async (msgId: string): Promise<void> => {
    const res = await fetchWithRetry(`${API_BASE}/crm-scheduled-messages/${msgId}/cancel`, { method: 'PATCH' });
    if (!res.ok) throw new Error('Falha ao cancelar agendamento');
  },

  getKanbanColumns: async (): Promise<KanbanColumn[]> => {
    const res = await fetchWithRetry(`${API_BASE}/kanban/columns`);
    if (!res.ok) throw new Error('Falha ao buscar colunas do kanban');
    return res.json();
  },

  createKanbanColumn: async (column: Partial<KanbanColumn>): Promise<KanbanColumn> => {
    const res = await fetchWithRetry(`${API_BASE}/kanban/columns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(column)
    });
    if (!res.ok) throw new Error('Falha ao criar coluna do kanban');
    return res.json();
  },

  updateKanbanColumn: async (id: string, updates: Partial<KanbanColumn>): Promise<KanbanColumn> => {
    const res = await fetchWithRetry(`${API_BASE}/kanban/columns/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Falha ao atualizar coluna do kanban');
    return res.json();
  },

  deleteKanbanColumn: async (id: string): Promise<void> => {
    const res = await fetchWithRetry(`${API_BASE}/kanban/columns/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Falha ao desativar coluna do kanban');
  },

  updateMessage: async (msgId: string, updates: { is_pinned?: boolean; is_deleted?: boolean; send_status?: string }): Promise<Message> => {
    const res = await fetchWithRetry(`${API_BASE}/messages/${msgId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Falha ao atualizar mensagem');
    return res.json();
  }
};

